import { useEffect, useState } from "react";
import { t } from "../i18n";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Crown, Pencil, Trash2, Phone, AtSign, Cake, MapPin, Languages, Handshake, Flag as FlagIcon } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { Shell } from "../components/Shell";
import { Sheet } from "../components/Sheet";
import { Avatar, Flag, RedFlagIcon, RED } from "../components/Avatar";
import { useModals } from "../context/ModalsContext";
import { Timeline } from "../components/Timeline";
import { countryName, fmtFecha, fmtRelativo, ESTADOS, idiomaLabel } from "../lib/data";
import { getLocale } from "../i18n";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const Row = ({ Icon, label, value, testId }) => value ? (
  <div className="flex items-start gap-3 py-2.5 border-b border-border last:border-0" data-testid={testId}>
    <Icon size={18} className="text-muted-foreground mt-0.5 shrink-0" />
    <div className="min-w-0"><p className="text-xs text-muted-foreground">{label}</p><p className="text-sm break-words">{value}</p></div>
  </div>
) : null;

const Stat = ({ label, value, testId }) => (
  <div className="text-center" data-testid={testId}><p className="text-lg font-display font-medium">{value}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p></div>
);

export default function PersonaDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { version } = useModals();
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get(`/people/${id}`).then((r) => setP(r.data)).catch(() => navigate("/personas")); }, [id, navigate, version]);

  const toggleCoronada = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/people/${id}/coronada`, { activa: !p.coronada?.activa });
      setP(data);
      toast.success(data.coronada.activa ? t("person.crowned") : t("person.crownRemoved"));
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const [rfModal, setRfModal] = useState(false);
  const [motivo, setMotivo] = useState("");
  const setRedFlag = async (activa, motivoTxt) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/people/${id}/red-flag`, { activa, motivo: motivoTxt || null });
      setP(data);
      setRfModal(false); setMotivo("");
      toast.success(activa ? t("person.redFlagOn") : t("person.redFlagOff"));
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };
  const onRedFlagChange = () => (p.red_flag?.activa ? setRedFlag(false) : setRfModal(true));

  const remove = async () => {
    try { await api.delete(`/people/${id}`); toast.success(t("person.deleted")); navigate("/personas", { replace: true }); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  if (!p) return <Shell title="Persona" back="/personas" fab={false}><p className="text-sm text-muted-foreground">{t("common.loading")}</p></Shell>;

  const cumple = p.cumple?.dia && p.cumple?.mes ? new Date(p.cumple.anio || 2024, p.cumple.mes - 1, p.cumple.dia).toLocaleDateString(getLocale(), { day: "numeric", month: "long", ...(p.cumple.anio ? { year: "numeric" } : {}) }) : null;
  const notas = Object.entries(p.notas || {}).filter(([, v]) => v);
  const coronada = p.coronada?.activa;
  const redFlag = p.red_flag?.activa;

  return (
    <Shell title={p.nombre} back="/personas" right={
      <Link to={`/personas/${id}/editar`} data-testid="person-edit-btn" className="p-2 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground"><Pencil size={19} /></Link>
    }>
      <div className="fade-up space-y-4" data-testid="person-detail">
        {redFlag && (
          <div data-testid="red-flag-banner" className="rounded-xl border p-3 text-sm flex items-start gap-2" style={{ borderColor: `${RED}66`, background: `${RED}1a`, color: "#ff8a8f" }}>
            <RedFlagIcon size={16} testId="red-flag-banner-icon" className="mt-0.5" />
            <span>{t("person.redFlagBanner", { m: p.red_flag.motivo ? ` · ${p.red_flag.motivo}` : "", d: fmtFecha(p.red_flag.fecha) })}</span>
          </div>
        )}
        <div className="flex items-center gap-4">
          <Avatar nombre={p.nombre} src={p.foto_url} coronada={coronada} redFlag={redFlag} size={88} testId="detail-avatar" />
          <div className="min-w-0 flex-1">
            <h2 data-testid="detail-name" className="text-2xl font-medium flex items-center gap-2">{p.nombre} <Flag iso={p.pais_iso} size={22} /></h2>
            {p.apodo && <p className="text-muted-foreground">"{p.apodo}"</p>}
            <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1"><MapPin size={14} /> {p.ciudad_vive?.nombre || "—"}{p.pais_iso ? ` · ${countryName(p.pais_iso)}` : ""}</p>
            <div className="flex items-center gap-2 mt-2">
              <span data-testid="detail-estado" className="text-[10px] uppercase tracking-wider border border-border rounded-full px-2 py-0.5 text-muted-foreground">{ESTADOS.find((e) => e.value === p.estado)?.label || p.estado}</span>
              {coronada && <span data-testid="detail-crown-badge" className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-[#d4af37]/15 text-[#f3c649] flex items-center gap-1"><Crown size={11} /> {t("person.crowned")}</span>}
            </div>
          </div>
        </div>

        <label data-testid="coronada-toggle" className={`surface p-4 flex items-center gap-3 cursor-pointer transition-colors ${coronada ? "border-[#d4af37]/50 bg-[#d4af37]/10" : ""}`}>
          <input type="checkbox" data-testid="coronada-checkbox" className="w-5 h-5 accent-[#d4af37]" checked={!!coronada} disabled={busy} onChange={toggleCoronada} />
          <Crown size={20} className={coronada ? "text-[#d4af37] gold-glow" : "text-muted-foreground"} fill={coronada ? "#d4af37" : "none"} />
          <span className="flex-1 font-medium">{t("person.crowned")}</span>
          {coronada && <span data-testid="coronada-fecha" className="text-xs text-muted-foreground">{fmtFecha(p.coronada.fecha)}</span>}
        </label>

        <label data-testid="red-flag-toggle" className="surface p-4 flex items-center gap-3 cursor-pointer transition-colors" style={redFlag ? { borderColor: `${RED}80`, background: `${RED}1a` } : undefined}>
          <input type="checkbox" data-testid="red-flag-checkbox" className="w-5 h-5" style={{ accentColor: RED }} checked={!!redFlag} disabled={busy} onChange={onRedFlagChange} />
          <FlagIcon size={20} style={{ color: redFlag ? RED : undefined }} className={redFlag ? "" : "text-muted-foreground"} fill={redFlag ? RED : "none"} />
          <span className="flex-1 font-medium">{t("person.redFlag")}</span>
          {redFlag && <span data-testid="red-flag-fecha" className="text-xs text-muted-foreground">{fmtFecha(p.red_flag.fecha)}</span>}
        </label>

        <Sheet open={rfModal} onClose={() => setRfModal(false)} title={t("person.redFlagActivate")} testId="red-flag-modal">
          <form onSubmit={(e) => { e.preventDefault(); setRedFlag(true, motivo.trim()); }} className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("person.redFlagDesc", { n: p.nombre })}</p>
            <div>
              <label className="label">{t("person.reason")}</label>
              <input data-testid="red-flag-motivo-input" className="field" placeholder={t("person.whatHappened")} value={motivo} onChange={(e) => setMotivo(e.target.value)} maxLength={300} autoFocus />
            </div>
            <button data-testid="red-flag-confirm-btn" disabled={busy} className="w-full rounded-full text-white font-semibold py-3.5 disabled:opacity-40" style={{ background: RED }}>{busy ? t("common.saving") : t("person.redFlagConfirm")}</button>
          </form>
        </Sheet>

        <div className="surface p-4 grid grid-cols-3 gap-y-4" data-testid="summary-block">
          <Stat label={t("person.lastPhysical")} value={fmtRelativo(p.ultima_fisica)} testId="stat-ultima-fisica" />
          <Stat label={t("person.lastChat")} value={fmtRelativo(p.ultima_conversacion)} testId="stat-ultima-conversacion" />
          <Stat label={t("stat.score")} value={<>{Number(p.score ?? 0).toFixed(1)} <span className="text-xs text-muted-foreground" data-testid="stat-tendencia">{p.score_tendencia === "sube" ? "↑" : p.score_tendencia === "baja" ? "↓" : "="}</span></>} testId="stat-score" />
          <Stat label={t("stat.regalos")} value={p.num_regalos ?? 0} testId="stat-regalos" />
          <Stat label={t("stat.viajes")} value={p.num_viajes ?? 0} testId="stat-viajes" />
          <Stat label={t("stat.eventos")} value={p.num_eventos ?? 0} testId="stat-eventos" />
        </div>

        <div className="surface px-4 py-1">
          <Row Icon={Cake} label={t("common.birthday")} value={cumple} testId="row-cumple" />
          <Row Icon={Languages} label={t("person.languages")} value={p.idiomas?.map(idiomaLabel).join(", ")} testId="row-idiomas" />
          <Row Icon={Handshake} label={t("person.howMet")} value={[p.conocidos?.fecha && fmtFecha(p.conocidos.fecha), p.conocidos?.lugar, p.conocidos?.como].filter(Boolean).join(" · ")} testId="row-conocidos" />
          <Row Icon={Phone} label={t("common.phone")} value={p.telefono} testId="row-telefono" />
          <Row Icon={AtSign} label={t("person.social")} value={p.redes} testId="row-redes" />
        </div>

        {p.etiquetas?.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid="detail-etiquetas">{p.etiquetas.map((et) => <span key={et} className="chip">#{et}</span>)}</div>
        )}

        {notas.length > 0 && (
          <div className="surface p-4 space-y-3" data-testid="detail-notas">
            <h3 className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{t("common.notes")}</h3>
            {notas.map(([k, v]) => <div key={k}><p className="text-xs text-muted-foreground">{t(`notas.${k}`, { defaultValue: k })}</p><p className="text-sm">{v}</p></div>)}
          </div>
        )}

        <Timeline person={p} />

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="person-delete-btn" className="btn-ghost w-full flex items-center justify-center gap-2 text-[#f43f5e] border-[#f43f5e]/30"><Trash2 size={16} /> {t("person.deleteTitle")}</button>
          </AlertDialogTrigger>
          <AlertDialogContent className="max-w-[360px] rounded-2xl">
            <AlertDialogHeader><AlertDialogTitle>{t("person.deleteConfirm", { n: p.nombre })}</AlertDialogTitle><AlertDialogDescription>{t("person.deleteWarn")}</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-cancel-btn">{t("common.cancel")}</AlertDialogCancel>
              <AlertDialogAction data-testid="delete-confirm-btn" onClick={remove} className="bg-[#f43f5e] text-white hover:bg-[#f43f5e]/90">{t("common.delete")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Shell>
  );
}
